import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  HostListener,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { PartnerContractPartyType } from '@interfaces/partners/contracts/responses/partner-contract.response';
import {
  PartnerContractTemplateResponse,
  PartnerContractTemplateVersionResponse,
} from '@interfaces/partners/contracts/responses/partner-contract-template.response';
import { ConfirmationService } from '@services/helpers/confirmation.service';
import { ToastHelperService } from '@services/helpers/toast-helper.service';
import { PartnerContractsHttpService } from '@services/http/partners/partner-contracts-http.service';
import { RichTextEditorComponent } from '@src/app/shared/components/rich-text-editor/rich-text-editor.component';
import { RichTextVariable } from '@src/app/shared/components/rich-text-editor/rich-text-editor.models';
import { VaultDateTimePipe } from '@src/app/shared/pipes/vault-date-time.pipe';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { Observable, first } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { PartnerContractUnsavedChanges } from '../../guards/partner-contract-unsaved-changes.guard';
import {
  CONTRACT_TEXT_LOCKED_MESSAGE,
  backendErrorMessage,
} from '../../utils/partner-contract.utils';
import {
  ContractTemplateVersionListItem,
  PartnerContractTemplateHistoryComponent,
} from './components/partner-contract-template-history/partner-contract-template-history.component';

/**
 * Modelo base do Contrato de Parceria Comercial (card 3462), versionado. Só **pessoa jurídica** desde o
 * card 3848 (pedido do Breno, 31/10/2026): a aba Pessoa física saiu. O backend continua com o tipo
 * `Individual`, para as minutas de pessoa física já geradas seguirem abrindo.
 *
 * Salvar cria uma versão nova; as anteriores ficam no histórico, só leitura, e podem servir de
 * base ("Usar como base" copia o texto para o editor). As minutas já geradas não mudam.
 *
 * Texto travado (card 3643): além de `PARTNERCONTRACT_MANAGE_TEMPLATE`, só quem está na lista de
 * editores do texto (Parametrização › Comercial) altera o modelo (`canEditText` do DTO). Os demais
 * veem o modelo só para leitura, com o aviso de quem pode alterar.
 */
@Component({
  selector: 'app-partner-contract-template',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SharedModule,
    VaultDateTimePipe,
    RichTextEditorComponent,
    PartnerContractTemplateHistoryComponent,
  ],
  templateUrl: './partner-contract-template.component.html',
  styleUrls: ['./partner-contract-template.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnerContractTemplateComponent implements OnInit, PartnerContractUnsavedChanges {
  private readonly contractsService = inject(PartnerContractsHttpService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly toast = inject(ToastHelperService);
  private readonly destroyRef = inject(DestroyRef);

  readonly home: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard/dashboard-management' };
  readonly breadcrumbs: MenuItem[] = [
    { label: 'Gerenciamento' },
    { label: 'Parceiros', routerLink: '/dashboard/partner/list' },
    { label: 'Modelo de contrato' },
  ];

  /** Exemplo exibido no aviso (no template, `{{` seria lido como interpolação). */
  readonly variableExample = '{{chave}}';

  /** Texto em edição (base: a versão vigente). */
  readonly bodyControl = new FormControl<string>('', { nonNullable: true });
  readonly changeNoteControl = new FormControl<string>('', { nonNullable: true });
  /** Texto da versão antiga aberta em leitura. */
  readonly viewingBodyControl = new FormControl<string>('', { nonNullable: true });

  readonly current = signal<PartnerContractTemplateResponse | null>(null);
  readonly versions = signal<PartnerContractTemplateVersionResponse[]>([]);
  readonly variables = signal<RichTextVariable[]>([]);
  /** Versão antiga aberta em leitura; `null` = editando. */
  readonly viewing = signal<PartnerContractTemplateResponse | null>(null);

  readonly loading = signal(true);
  readonly versionsLoading = signal(true);
  readonly saving = signal(false);
  readonly dirty = signal(false);
  readonly historyOpen = signal(true);

  /** Tipo de parte do modelo: só pessoa jurídica (card 3848). */
  readonly partyType: PartnerContractPartyType = 'Company';
  /** O usuário está na lista de editores do texto, pelo último modelo lido. `null` = ainda não se sabe. */
  readonly canEditText = signal<boolean | null>(null);
  /** Modelo só para leitura: o backend disse que o usuário não está na lista de editores. */
  readonly readOnly = computed(() => this.canEditText() === false);
  readonly textLockedMessage = CONTRACT_TEXT_LOCKED_MESSAGE;

  ngOnInit(): void {
    this.bodyControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.dirty.set(this.bodyControl.dirty));

    this.loadCurrent();
    this.loadVersions();

    this.contractsService
      .getVariables()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: list => this.variables.set(list), error: () => this.variables.set([]) });
  }

  toggleHistory(): void {
    this.historyOpen.update(open => !open);
  }

  /** Clique no histórico: a vigente volta ao editor; uma antiga abre em leitura. */
  openVersion(version: ContractTemplateVersionListItem): void {
    if (version.isCurrent) {
      this.viewing.set(null);
      return;
    }

    this.contractsService
      .getTemplateVersion(version.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: template => {
          if (!template) return;
          this.viewingBodyControl.setValue(template.bodyHtml ?? '');
          this.viewing.set(template);
        },
        error: error =>
          this.toast.showToastError(
            `Não foi possível abrir a versão ${version.version}`,
            backendErrorMessage(error) || 'Clique na versão de novo em instantes.'
          ),
      });
  }

  closeViewing(): void {
    this.viewing.set(null);
  }

  /** Copia o texto da versão antiga para o editor (vira o rascunho da próxima versão). */
  useViewingAsBase(): void {
    const template = this.viewing();
    if (!template || this.readOnly()) return;

    this.bodyControl.setValue(template.bodyHtml ?? '');
    this.bodyControl.markAsDirty();
    this.dirty.set(true);
    if (!this.changeNoteControl.value) {
      this.changeNoteControl.setValue(`Baseada na versão ${template.version}`);
    }
    this.viewing.set(null);
    this.toast.showToastInfo(
      `Texto da versão ${template.version} carregado`,
      'Revise e use "Salvar nova versão" para torná-lo o modelo vigente.'
    );
  }

  canSave(): boolean {
    return (
      !this.readOnly() &&
      this.dirty() &&
      !this.saving() &&
      !this.viewing() &&
      !!this.bodyControl.value.trim()
    );
  }

  save(): void {
    if (!this.canSave()) return;

    this.confirmationService
      .confirm(
        'Salvar nova versão do modelo?',
        'Os contratos já gerados não mudam. Os próximos usarão esta versão.',
        'default',
        'Cancelar',
        'Salvar nova versão'
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(confirmed => {
        if (confirmed) this.persist();
      });
  }

  @HostListener('document:keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 's') {
      event.preventDefault();
      this.save();
    }
  }

  confirmLeave(): boolean | Observable<boolean> {
    if (!this.dirty()) return true;
    return this.confirmationService
      .confirm(
        'Sair sem salvar?',
        'As mudanças no modelo ainda não foram salvas e serão perdidas.',
        'danger',
        'Continuar editando',
        'Sair sem salvar'
      )
      .pipe(first());
  }

  @HostListener('window:beforeunload', ['$event'])
  protected onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.dirty()) {
      event.preventDefault();
      event.returnValue = '';
    }
  }

  private persist(): void {
    this.saving.set(true);
    const changeNote = this.changeNoteControl.value.trim();
    const sentBody = this.bodyControl.value;

    this.contractsService
      .saveTemplateVersion({
        partyType: this.partyType,
        bodyHtml: sentBody,
        changeNote: changeNote || null,
      })
      .pipe(
        finalize(() => this.saving.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: template => {
          this.toast.showToastSuccess(
            template ? `Versão ${template.version} salva` : 'Nova versão salva',
            'Os próximos contratos gerados já usam este texto.'
          );
          // Se a pessoa continuou digitando durante o salvamento, não sobrescrever o editor: o texto
          // novo ficaria perdido sem aviso. Só a versão vigente é atualizada, e a tela segue alterada.
          if (template && this.bodyControl.value === sentBody) {
            this.applyCurrent(template, { keepBody: true });
          } else if (template) {
            this.current.set(template);
          }
          this.changeNoteControl.setValue('');
          this.loadVersions();
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível salvar a nova versão',
            backendErrorMessage(error) || 'O texto continua aqui; tente salvar de novo.'
          ),
      });
  }

  private loadCurrent(): void {
    this.loading.set(true);
    this.contractsService
      .getCurrentTemplate(this.partyType)
      .pipe(
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: template => {
          if (template) this.applyCurrent(template);
        },
        error: error =>
          this.toast.showToastError(
            'Não foi possível carregar o modelo do contrato',
            backendErrorMessage(error) || 'Recarregue a página para tentar de novo.'
          ),
      });
  }

  private loadVersions(): void {
    this.versionsLoading.set(true);
    this.contractsService
      .getTemplateVersions(this.partyType)
      .pipe(
        finalize(() => this.versionsLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: list => this.versions.set([...list].sort((a, b) => b.version - a.version)),
        error: () => this.versions.set([]),
      });
  }

  /** Põe o modelo no editor. `keepBody` evita regravar o corpo (preserva cursor e desfazer). */
  private applyCurrent(
    template: PartnerContractTemplateResponse,
    options: { keepBody?: boolean } = {}
  ): void {
    this.current.set(template);
    this.canEditText.set(template.canEditText === true);
    if (!options.keepBody) {
      this.bodyControl.setValue(template.bodyHtml ?? '', { emitEvent: false });
    }
    this.bodyControl.markAsPristine();
    this.dirty.set(false);
  }
}
